import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { weekLabel, volledigeDatum } from '../utils/datum';

const STATUS_NL = { gepland: 'Gepland', in_uitvoering: 'In uitvoering', on_hold: 'On hold', afgewerkt: 'Afgewerkt', voltooid: 'Voltooid', geannuleerd: 'Geannuleerd' };
const STATUS_CL = { gepland: '#0C447C', in_uitvoering: '#27500A', on_hold: '#7F6000', afgewerkt: '#1A7A3C', voltooid: '#444', geannuleerd: '#791F1F' };
const STATUS_BG = { gepland: '#E6F1FB', in_uitvoering: '#EAF3DE', on_hold: '#FFF2CC', afgewerkt: '#EAF3DE', voltooid: '#F1EFE8', geannuleerd: '#FCEBEB' };
const AFGEWERKT_STATUSSEN = ['afgewerkt', 'voltooid', 'geannuleerd'];
const MAT_NL  = { in_stock: 'In stock', te_bestellen: 'Te bestellen', besteld: 'Besteld', geleverd: 'Geleverd' };
const MAT_CL  = { in_stock: '#1A7A3C', te_bestellen: '#C0392B', besteld: '#B5660E', geleverd: '#185FA5' };
const MAT_BG  = { in_stock: '#EAF3DE', te_bestellen: '#FDEDEC', besteld: '#FDF3E3', geleverd: '#E6F1FB' };
const PRIO_CL = { spoed: '#A32D2D', hoog: '#854F0B', normaal: '#3B6D11' };
const PRIO_BG = { spoed: '#FDEDEC', hoog: '#FDF3E3', normaal: '#EAF3DE' };

function begroeting(naam) {
  const uur = new Date().getHours();
  const groet = uur < 12 ? 'Goedemorgen' : uur < 18 ? 'Goedemiddag' : 'Goedenavond';
  return `${groet}, ${naam}`;
}

function KpiKaart({ label, waarde, subLabel, kleur, bg, onClick }) {
  return (
    <div onClick={onClick} style={{
      background: bg || '#fff', border: '1px solid #E0E0E0', borderRadius: 10,
      padding: '16px 20px', flex: 1, minWidth: 140,
      cursor: onClick ? 'pointer' : 'default',
      borderLeft: kleur ? `4px solid ${kleur}` : '1px solid #E0E0E0',
      transition: 'box-shadow 0.15s',
    }}
      onMouseEnter={e => { if (onClick) e.currentTarget.style.boxShadow = '0 2px 10px rgba(0,0,0,0.1)'; }}
      onMouseLeave={e => { e.currentTarget.style.boxShadow = 'none'; }}
    >
      <div style={{ fontSize: 11, color: '#888', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.5px' }}>{label}</div>
      <div style={{ fontSize: 32, fontWeight: 700, color: kleur || '#1F3864', lineHeight: 1 }}>{waarde}</div>
      {subLabel && <div style={{ fontSize: 11, color: '#AAA', marginTop: 4 }}>{subLabel}</div>}
    </div>
  );
}

function BarRij({ label, aantal, totaal, kleur, bg }) {
  const pct = totaal > 0 ? Math.round((aantal / totaal) * 100) : 0;
  return (
    <div style={{ marginBottom: 10 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
        <span style={{ fontSize: 12, color: '#555' }}>{label}</span>
        <span style={{ fontSize: 12, fontWeight: 600, color: kleur }}>{aantal} <span style={{ color: '#AAA', fontWeight: 400 }}>({pct}%)</span></span>
      </div>
      <div style={{ height: 8, background: '#F0F0F0', borderRadius: 99 }}>
        <div style={{ width: `${pct}%`, height: 8, background: kleur, borderRadius: 99, transition: 'width 0.4s ease' }} />
      </div>
    </div>
  );
}

export default function DashboardPage({ user }) {
  const [orders, setOrders] = useState([]);
  const [laden, setLaden] = useState(true);
  const nav = useNavigate();

  useEffect(() => {
    api.get('/orders').then(r => { setOrders(r.data); setLaden(false); }).catch(() => setLaden(false));
  }, []);

  const actief      = orders.filter(o => !AFGEWERKT_STATUSSEN.includes(o.status));
  const inUitvoering = orders.filter(o => o.status === 'in_uitvoering');
  const gepland     = orders.filter(o => o.status === 'gepland');
  const onHold      = orders.filter(o => o.status === 'on_hold');
  const teLaat      = actief.filter(o => o.dagen_resterend < 0);
  const vandaagMorgen = actief.filter(o => o.dagen_resterend >= 0 && o.dagen_resterend <= 1);
  const matWacht    = actief.filter(o => ['te_bestellen', 'besteld'].includes(o.materiaal_status));
  const spoed       = actief.filter(o => o.prioriteit === 'spoed');

  const urgent = [...actief]
    .filter(o => o.dagen_resterend <= 7)
    .sort((a, b) => a.dagen_resterend - b.dagen_resterend)
    .slice(0, 8);

  const recentActief = [...inUitvoering]
    .sort((a, b) => {
      const va = a.totaal_stappen > 0 ? a.voltooide_stappen / a.totaal_stappen : 0;
      const vb = b.totaal_stappen > 0 ? b.voltooide_stappen / b.totaal_stappen : 0;
      return va - vb;
    })
    .slice(0, 8);

  const familieTelling = {};
  actief.forEach(o => {
    if (o.familie_naam) {
      if (!familieTelling[o.familie_naam]) familieTelling[o.familie_naam] = { aantal: 0, kleur: o.familie_kleur };
      familieTelling[o.familie_naam].aantal++;
    }
  });
  const families = Object.entries(familieTelling).sort((a, b) => b[1].aantal - a[1].aantal);

  const dagLabel = (d) => {
    if (d < 0) return <span style={{ color: '#A32D2D', fontWeight: 700 }}>{Math.abs(d)}d te laat</span>;
    if (d === 0) return <span style={{ color: '#854F0B', fontWeight: 700 }}>Vandaag!</span>;
    if (d === 1) return <span style={{ color: '#854F0B', fontWeight: 600 }}>Morgen</span>;
    return <span style={{ color: '#3B6D11' }}>{d} dagen</span>;
  };

  const kaart = { background: '#fff', border: '1px solid #E0E0E0', borderRadius: 10, marginBottom: 16 };
  const kaartHdr = { padding: '12px 18px', borderBottom: '1px solid #F0F0F0', fontWeight: 600, fontSize: 14, color: '#1F3864', display: 'flex', justifyContent: 'space-between', alignItems: 'center' };

  return (
    <div style={{ padding: 24, maxWidth: 1200 }}>

      {/* Greeting */}
      <div style={{ marginBottom: 22 }}>
        <h1 style={{ margin: 0, fontSize: 20, fontWeight: 600, color: '#1F3864' }}>Dashboard</h1>
        <div style={{ fontSize: 13, color: '#888', marginTop: 2 }}>
          {begroeting(user?.naam)} — {new Date().toLocaleDateString('nl-BE', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
        </div>
      </div>

      {/* KPI kaarten */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
        <KpiKaart label="Actieve orders" waarde={laden ? '…' : actief.length}
          subLabel={`${gepland.length} gepland · ${inUitvoering.length} in uitvoering`}
          kleur="#185FA5" onClick={() => nav('/orders?status=actief')} />
        <KpiKaart label="Te laat" waarde={laden ? '…' : teLaat.length}
          subLabel={teLaat.length === 0 ? 'Alles op schema' : 'Overschreden deadline'}
          kleur={teLaat.length > 0 ? '#A32D2D' : '#3B6D11'} onClick={() => nav('/orders')} />
        <KpiKaart label="Deadline vandaag/morgen" waarde={laden ? '…' : vandaagMorgen.length}
          subLabel={vandaagMorgen.length === 0 ? 'Geen directe deadlines' : 'Dringend afronden'}
          kleur={vandaagMorgen.length > 0 ? '#854F0B' : '#3B6D11'} />
        <KpiKaart label="Wacht op materiaal" waarde={laden ? '…' : matWacht.length}
          subLabel={matWacht.length === 0 ? 'Alles beschikbaar' : 'Te bestellen of besteld'}
          kleur={matWacht.length > 0 ? '#854F0B' : '#3B6D11'} onClick={() => nav('/orders')} />
        <KpiKaart label="Spoed" waarde={laden ? '…' : spoed.length}
          subLabel={spoed.length === 0 ? 'Geen spoedorders' : 'Hoogste prioriteit'}
          kleur={spoed.length > 0 ? '#A32D2D' : '#3B6D11'} />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 340px', gap: 16, marginBottom: 0 }}>

        {/* Urgente orders */}
        <div style={kaart}>
          <div style={kaartHdr}>
            <span>Urgente orders <span style={{ fontSize: 11, fontWeight: 400, color: '#888' }}>(deadline binnen 7 dagen)</span></span>
            <button onClick={() => nav('/orders/nieuw')} style={{ fontSize: 12, padding: '4px 12px', background: '#185FA5', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer' }}>
              + Nieuw order
            </button>
          </div>
          {laden ? (
            <div style={{ padding: 24, color: '#888', fontSize: 13 }}>Laden...</div>
          ) : urgent.length === 0 ? (
            <div style={{ padding: 24, color: '#3B6D11', fontSize: 13 }}>Geen urgente orders — alles op schema!</div>
          ) : urgent.map(o => {
            const voortgang = o.totaal_stappen > 0 ? Math.round((o.voltooide_stappen / o.totaal_stappen) * 100) : 0;
            return (
              <div key={o.id} onClick={() => nav(`/orders/${o.id}`)}
                style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 18px', borderBottom: '1px solid #F5F5F5', cursor: 'pointer' }}
                onMouseEnter={e => e.currentTarget.style.background = '#F5F9FF'}
                onMouseLeave={e => e.currentTarget.style.background = '#fff'}>
                {o.familie_kleur && <div style={{ width: 4, height: 32, borderRadius: 2, background: o.familie_kleur, flexShrink: 0 }} />}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                    <span style={{ fontSize: 12, fontWeight: 700, color: '#185FA5' }}>{o.ordernummer}</span>
                    <span style={{ fontSize: 11, padding: '1px 7px', borderRadius: 99, background: PRIO_BG[o.prioriteit], color: PRIO_CL[o.prioriteit] }}>{o.prioriteit}</span>
                    {o.materiaal_status && !['in_stock', 'geleverd'].includes(o.materiaal_status) && (
                      <span style={{ fontSize: 11, padding: '1px 7px', borderRadius: 99, background: MAT_BG[o.materiaal_status], color: MAT_CL[o.materiaal_status] }}>
                        {MAT_NL[o.materiaal_status]}
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize: 12, color: '#555', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {o.klant} — {o.artikel_omschrijving}
                  </div>
                  <div style={{ marginTop: 5, display: 'flex', alignItems: 'center', gap: 8 }}>
                    <div style={{ flex: 1, height: 4, background: '#EEE', borderRadius: 99 }}>
                      <div style={{ width: `${voortgang}%`, height: 4, background: voortgang === 100 ? '#3B6D11' : '#185FA5', borderRadius: 99 }} />
                    </div>
                    <span style={{ fontSize: 10, color: '#AAA', minWidth: 28 }}>{voortgang}%</span>
                  </div>
                </div>
                <div style={{ textAlign: 'right', flexShrink: 0 }}>
                  <div style={{ fontSize: 12, marginBottom: 2 }}>{dagLabel(o.dagen_resterend)}</div>
                  <div style={{ fontSize: 11, color: '#AAA' }} title={volledigeDatum(o.leverdatum)}>{weekLabel(o.leverdatum)}</div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Rechter kolom */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

          {/* Status verdeling */}
          <div style={kaart}>
            <div style={kaartHdr}>Status verdeling</div>
            <div style={{ padding: '14px 18px' }}>
              {laden ? <div style={{ color: '#888', fontSize: 13 }}>Laden...</div> : (
                <>
                  <BarRij label="In uitvoering" aantal={inUitvoering.length} totaal={actief.length} kleur="#3B6D11" />
                  <BarRij label="Gepland"        aantal={gepland.length}      totaal={actief.length} kleur="#185FA5" />
                  <BarRij label="On hold"         aantal={onHold.length}       totaal={actief.length} kleur="#7F6000" />
                  <BarRij label="Te laat"          aantal={teLaat.length}      totaal={actief.length} kleur="#A32D2D" />
                </>
              )}
            </div>
          </div>

          {/* Materiaalstatus */}
          <div style={kaart}>
            <div style={kaartHdr}>Materiaal (actieve orders)</div>
            <div style={{ padding: '14px 18px' }}>
              {laden ? <div style={{ color: '#888', fontSize: 13 }}>Laden...</div> : (
                ['te_bestellen', 'besteld', 'geleverd', 'in_stock'].map(ms => {
                  const n = actief.filter(o => o.materiaal_status === ms).length;
                  return <BarRij key={ms} label={MAT_NL[ms]} aantal={n} totaal={actief.length} kleur={MAT_CL[ms]} />;
                })
              )}
            </div>
          </div>

          {/* Productfamilies */}
          {families.length > 0 && (
            <div style={kaart}>
              <div style={kaartHdr}>Per productfamilie</div>
              <div style={{ padding: '10px 18px' }}>
                {families.slice(0, 6).map(([naam, { aantal, kleur }]) => (
                  <div key={naam} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                    <div style={{ width: 10, height: 10, borderRadius: 99, background: kleur || '#CCC', flexShrink: 0 }} />
                    <span style={{ flex: 1, fontSize: 12, color: '#444' }}>{naam}</span>
                    <span style={{ fontSize: 12, fontWeight: 600, color: '#1F3864' }}>{aantal}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Orders in uitvoering */}
      {recentActief.length > 0 && (
        <div style={{ ...kaart, marginTop: 16 }}>
          <div style={kaartHdr}>
            <span>In uitvoering <span style={{ fontWeight: 400, fontSize: 12, color: '#888' }}>(minst ver gevorderd)</span></span>
            <button onClick={() => nav('/orders')} style={{ fontSize: 12, color: '#185FA5', background: 'none', border: 'none', cursor: 'pointer' }}>Alle orders →</button>
          </div>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: '#F7F9FC' }}>
                {['Order nr.', 'Klant', 'Artikel', 'Leverdatum', 'Prioriteit', 'Materiaal', 'Voortgang'].map(h => (
                  <th key={h} style={{ padding: '8px 14px', textAlign: 'left', fontSize: 11, color: '#888', fontWeight: 500 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {recentActief.map((o, i) => {
                const voortgang = o.totaal_stappen > 0 ? Math.round((o.voltooide_stappen / o.totaal_stappen) * 100) : 0;
                return (
                  <tr key={o.id} onClick={() => nav(`/orders/${o.id}`)}
                    style={{ borderTop: '1px solid #F0F0F0', cursor: 'pointer' }}
                    onMouseEnter={e => e.currentTarget.style.background = '#F5F9FF'}
                    onMouseLeave={e => e.currentTarget.style.background = i % 2 === 0 ? '#fff' : '#FAFAFA'}>
                    <td style={{ padding: '9px 14px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        {o.familie_kleur && <div style={{ width: 4, height: 18, borderRadius: 2, background: o.familie_kleur }} />}
                        <span style={{ fontWeight: 600, color: '#185FA5' }}>{o.ordernummer}</span>
                      </div>
                    </td>
                    <td style={{ padding: '9px 14px', color: '#333' }}>{o.klant}</td>
                    <td style={{ padding: '9px 14px', color: '#555', maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{o.artikel_omschrijving}</td>
                    <td style={{ padding: '9px 14px', color: '#555', whiteSpace: 'nowrap' }}>
                      <span style={{ color: o.dagen_resterend < 0 ? '#A32D2D' : o.dagen_resterend <= 2 ? '#854F0B' : '#555' }}
                            title={volledigeDatum(o.leverdatum)}>
                        {weekLabel(o.leverdatum)}
                      </span>
                    </td>
                    <td style={{ padding: '9px 14px' }}>
                      <span style={{ fontSize: 11, padding: '1px 7px', borderRadius: 99, background: PRIO_BG[o.prioriteit], color: PRIO_CL[o.prioriteit] }}>
                        {o.prioriteit}
                      </span>
                    </td>
                    <td style={{ padding: '9px 14px' }}>
                      {o.materiaal_status && (
                        <span style={{ fontSize: 11, padding: '1px 7px', borderRadius: 99, background: MAT_BG[o.materiaal_status], color: MAT_CL[o.materiaal_status] }}>
                          {MAT_NL[o.materiaal_status]}
                        </span>
                      )}
                    </td>
                    <td style={{ padding: '9px 14px', minWidth: 120 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <div style={{ flex: 1, height: 6, background: '#EEE', borderRadius: 99 }}>
                          <div style={{ width: `${voortgang}%`, height: 6, background: voortgang === 100 ? '#3B6D11' : '#185FA5', borderRadius: 99 }} />
                        </div>
                        <span style={{ fontSize: 11, color: '#888', minWidth: 30, textAlign: 'right' }}>{voortgang}%</span>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
